// Exports the *currently filtered* stage view to a CSV spreadsheet.
//
// Why CSV (not xlsx): no extra dependency, opens cleanly in Excel/Sheets,
// preserves formulas/numbers, and matches the existing /api/line-items/export
// format that admins already know. Date-stamped filename keeps backups tidy.

const CSV_QUOTE = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  // Quote when the value contains characters that would break CSV parsing.
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const formatDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return String(raw);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
};

const sumSpend = (item, subteam) => {
  // Modern path: spend_entries audit array (excludes soft-deleted).
  if (Array.isArray(item.spend_entries) && item.spend_entries.length) {
    return item.spend_entries
      .filter(e => !e.deleted_at && (!subteam || e.subteam === subteam))
      .reduce((s, e) => s + (Number(e.amount) || 0), 0);
  }
  // Legacy fallback: per-subteam scalar fields.
  if (subteam) {
    const map = { Material: "material_spend", Tooling: "tooling_spend", Gauging: "gauging_spend" };
    return Number(item[map[subteam]] || 0);
  }
  return Number(item.material_spend || 0) + Number(item.tooling_spend || 0) + Number(item.gauging_spend || 0);
};

// Stage-specific column definitions — { header, accessor }
// The order matches operational priority for that stage.
const COMMON_COLUMNS = [
  { h: "PO Number",    a: i => i.po_number },
  { h: "Part Number",  a: i => i.part_number },
  { h: "Line",         a: i => i.line_number },
  { h: "Revision",     a: i => i.revision },
  { h: "Description",  a: i => i.item_description },
  { h: "Client",       a: i => i.client },
  { h: "Vendor",       a: i => i.vendor },
  { h: "Quantity",     a: i => i.quantity },
  { h: "Unit",         a: i => i.unit },
  { h: "Value (USD)",  a: i => i.value_usd },
  { h: "Priority",     a: i => i.priority },
  { h: "PO Date",      a: i => formatDate(i.po_date) },
  { h: "Due Date",     a: i => formatDate(i.due_date) },
  { h: "Dispatch Due", a: i => formatDate(i.dispatch_due_date) },
  { h: "Stage",        a: i => i.stage },
];

const STAGE_EXTRAS = {
  "PO Received": [
    { h: "QUAL",          a: i => i.is_qual ? "Yes" : "No" },
    { h: "Stage Comment", a: i => i.stage_comment },
  ],
  "Procurement": [
    { h: "Material Status",  a: i => i.material_status || "Pending" },
    { h: "Material ETA",     a: i => formatDate(i.material_eta) },
    { h: "Material Spend",   a: i => sumSpend(i, "Material") },
    { h: "Tooling Status",   a: i => i.tooling_status || "Pending" },
    { h: "Tooling ETA",      a: i => formatDate(i.tooling_eta) },
    { h: "Tooling Spend",    a: i => sumSpend(i, "Tooling") },
    { h: "Gauging Status",   a: i => i.gauging_status || "Pending" },
    { h: "Gauging ETA",      a: i => formatDate(i.gauging_eta) },
    { h: "Gauging Spend",    a: i => sumSpend(i, "Gauging") },
    { h: "Total Spend",      a: i => sumSpend(i) },
    { h: "Stage Comment",    a: i => i.stage_comment },
  ],
  "Manufacturing": [
    { h: "ERP Code",            a: i => i.erp_code || "" },
    { h: "Mfg Status",          a: i => i.manufacturing_status || "Queued" },
    { h: "Third Party Where",   a: i => i.third_party_where || "" },
    { h: "Third Party ETA",     a: i => formatDate(i.third_party_eta) },
    { h: "Route Card #",        a: i => i.router_card_number || "" },
    { h: "Route Released At",   a: i => formatDate(i.router_released_at) },
    { h: "Route Released By",   a: i => i.router_released_by || "" },
    { h: "Kitting Complete",    a: i => i.kitting_complete ? "Yes" : "No" },
    { h: "Kitting Confirmed At",a: i => formatDate(i.kitting_complete_at) },
    { h: "Kitting Confirmed By",a: i => i.kitting_complete_by || "" },
    { h: "Machine Used",        a: i => i.machine_used },
    { h: "Cycle Time",          a: i => i.cycle_time },
    { h: "Stage Comment",       a: i => i.stage_comment },
  ],
  "Quality Control": [
    { h: "ERP Code",             a: i => i.erp_code || "" },
    { h: "QC Status",            a: i => i.qc_status || "Pending Inspection" },
    { h: "Quality Dossier Ready",a: i => i.quality_dossier_ready ? "Yes" : "No" },
    { h: "Drawing Links",        a: i => Array.isArray(i.drawing_links) ? i.drawing_links.join("; ") : "" },
    { h: "Stage Comment",        a: i => i.stage_comment },
  ],
  "Dispatch": [
    { h: "ERP Code",        a: i => i.erp_code || "" },
    { h: "Dispatch Status", a: i => i.dispatch_status || "" },
    { h: "Carrier",         a: i => i.carrier_name },
    { h: "Tracking ID",     a: i => i.tracking_id },
    { h: "Shipped On",      a: i => formatDate(i.shipped_at) },
    { h: "Shipped By",      a: i => i.shipped_by || "" },
    { h: "Stage Comment",   a: i => i.stage_comment },
  ],
  "Completed": [
    { h: "ERP Code",        a: i => i.erp_code || "" },
    { h: "Mfg Status",      a: i => i.manufacturing_status },
    { h: "QC Status",       a: i => i.qc_status },
    { h: "Dispatch Status", a: i => i.dispatch_status },
    { h: "Carrier",         a: i => i.carrier_name },
    { h: "Tracking ID",     a: i => i.tracking_id },
    { h: "Shipped On",      a: i => formatDate(i.shipped_at) },
    { h: "Shipped By",      a: i => i.shipped_by || "" },
    { h: "Completed On",    a: i => formatDate(i?.stage_ages?.["Completed"]?.entered_at) },
  ],
};

const FLAG_COLUMNS = [
  { h: "Blocked",         a: i => i.is_blocked ? "Yes" : "No" },
  { h: "Block Code",      a: i => i.block_reason_code },
  { h: "Block Reason",    a: i => i.block_reason },
  { h: "Block Subteam",   a: i => i.block_reason_subteam },
  { h: "Next Action",     a: i => i.next_action },
  { h: "Next Action Date",a: i => formatDate(i.next_action_date) },
  { h: "Next Action By",  a: i => i.next_action_by },
  { h: "Rework",          a: i => i.is_rework ? "Yes" : "No" },
  { h: "Return Reason",   a: i => i.return_reason },
];

// Flatten user_notes into a single human-readable string — newest first. Each
// note is rendered as:  "[Author · Role · DD-Mon-YY] text  ETA: DD-Mon-YY"
// and joined with blank-line separators so Excel keeps them inside one cell.
const formatNotes = (notes) => {
  if (!Array.isArray(notes) || notes.length === 0) return "";
  const sorted = [...notes].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return sorted.map(n => {
    const when = formatDate(n.created_at);
    const header = `[${n.author || "Unknown"}${n.author_role ? " · " + n.author_role : ""}${when ? " · " + when : ""}]`;
    const body = (n.text || "").replace(/\r?\n/g, " ").trim();
    const eta = n.eta ? `  ETA: ${formatDate(n.eta)}` : "";
    return `${header} ${body}${eta}`;
  }).join("\n\n");
};

const NOTES_COLUMNS = [
  { h: "Notes Count", a: i => Array.isArray(i.user_notes) ? i.user_notes.length : 0 },
  { h: "Notes",       a: i => formatNotes(i.user_notes) },
];

/**
 * Build CSV text for the given items + stage and trigger a browser download.
 * @param {string} stage   Pipeline stage name
 * @param {Array}  items   Filtered items (across all pages) currently visible
 * @param {object} [opts]  showValue — when false, the Value (USD) column is dropped
 */
export function exportStageView(stage, items, opts = {}) {
  const { showValue = true } = opts;

  let baseColumns = COMMON_COLUMNS;
  if (!showValue) baseColumns = baseColumns.filter(c => c.h !== "Value (USD)");

  const columns = [
    ...baseColumns,
    ...(STAGE_EXTRAS[stage] || []),
    ...FLAG_COLUMNS,
    ...NOTES_COLUMNS,
  ];

  const headerRow = columns.map(c => CSV_QUOTE(c.h)).join(",");
  const dataRows = items.map(item =>
    columns.map(c => CSV_QUOTE(c.a(item) ?? "")).join(",")
  );

  // Prefix BOM so Excel detects UTF-8 (otherwise rupee/INR symbols misrender).
  const csv = "\uFEFF" + [headerRow, ...dataRows].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const now = new Date();
  const stamp = `${String(now.getDate()).padStart(2,"0")}-${months[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;
  const slug = stage.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "").toLowerCase();
  const filename = `rocklink_${slug}_${stamp}.csv`;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { rowCount: items.length, filename };
}
